import React, { useState } from "react";
import { styled, formGap, inlineTextIconMargin, InteractiveElement, muiInputFormsCSS } from "@iventis/styles";
import { useDispatch } from "react-redux";
import type { ThunkDispatch, Action } from "@reduxjs/toolkit";

import { Body2, Body1, Header4, Header5 } from "@iventis/styles/src/components/texts";
import { StyledFieldLabel } from "@iventis/styles/src/components/forms";
import { Alert, Button, TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Content } from "@iventis/translations/content/typed-content";
import { useProject } from "@iventis/permissions/src/use-project";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { AuthState, updateProjectDetails } from "@iventis/permissions/src/auth.slice";
import { getIventisEnvironmentData } from "@iventis/api/src/api";

export const ProjectDetails: React.FC = () => {
    const project = useProject();
    const translate = useIventisTranslate();
    const dispatch = useDispatch<ThunkDispatch<AuthState, typeof updateProjectDetails, Action<unknown>>>();
    const { appUrl, appProtocol } = getIventisEnvironmentData();
    const [editingDetails, setEditingDetails] = useState<boolean>(false);
    const [savingDetails, setSavingDetails] = useState<boolean>(false);
    const [nameInputValue, setNameInputValue] = useState<string>(project?.name || "");

    const trySaveDetails = async () => {
        setSavingDetails(true);
        await dispatch(updateProjectDetails({ name: nameInputValue }));
        setSavingDetails(false);
        setEditingDetails(false);
    };

    return (
        <>
            <ProjectDetailsHeader>
                <Header4>{translate(Content.settings.projectCustomisation.projectDetails)}</Header4>
                {!editingDetails && (
                    <InteractiveElement onClick={() => setEditingDetails(true)}>
                        <Body2 className="edit-details">{translate(Content.settings.projectCustomisation.editDetails)}</Body2>
                    </InteractiveElement>
                )}
            </ProjectDetailsHeader>
            {editingDetails ? (
                <ProjectDetailsEdit>
                    <StyledAlert severity="warning">
                        <Body1>{translate(Content.settings3.projectCustomisation.projectNameWarning)}</Body1>
                        <AlertRow>
                            <Body2>{`\u2022`}</Body2> <AlertListName>{translate(Content.settings3.projectCustomisation.projectNameWarningUrlKey)}</AlertListName>{" "}
                            <Body2>{appProtocol}</Body2>
                            <Body2Underlined>{project.shortName}</Body2Underlined>
                            <Body2>.{appUrl}</Body2>
                        </AlertRow>
                        <AlertRow>
                            <Body2>{`\u2022`}</Body2> <AlertListName>{translate(Content.settings3.projectCustomisation.projectNameWarningSsoKey)}</AlertListName>{" "}
                            <Body2>{translate(Content.settings3.projectCustomisation.projectNameWarningSsoValue)}</Body2>
                        </AlertRow>
                    </StyledAlert>
                    <StyledFieldLabel>{translate(Content.settings.projectCustomisation.projectName)}</StyledFieldLabel>
                    <TextField
                        variant="outlined"
                        className="edit-name-field"
                        disabled={savingDetails}
                        error={nameInputValue.length > 24}
                        helperText={translate(Content.settings.projectCustomisation.projectNameHelperText)}
                        value={nameInputValue}
                        onChange={(e) => setNameInputValue(e.target.value)}
                    />
                    <ProjectDetailsEditButtons>
                        <Button
                            disabled={savingDetails}
                            onClick={() => {
                                setEditingDetails(false);
                                setNameInputValue(project?.name);
                            }}
                        >
                            {translate(Content.common.buttons.cancel)}
                        </Button>
                        <Button
                            color="primary"
                            disabled={project?.name === nameInputValue || nameInputValue.length === 0 || nameInputValue.length > 24 || savingDetails}
                            onClick={() => trySaveDetails()}
                        >
                            {savingDetails && <FontAwesomeIcon icon={["fas", "circle-notch"]} spin style={{ marginRight: inlineTextIconMargin }} />}
                            <span>{translate(Content.common.buttons.save)}</span>
                        </Button>
                    </ProjectDetailsEditButtons>
                </ProjectDetailsEdit>
            ) : (
                <ProjectDetailsRead>
                    <Header5>{translate(Content.settings.projectCustomisation.projectName)}</Header5>
                    <Body2>{project?.name}</Body2>
                    {/* <Header5>Event start date</Header5>
                        <Body2>1st Oct 2021</Body2>
                        <Header5>Event end date</Header5>
                        <Body2>31 March 2022</Body2> */}
                </ProjectDetailsRead>
            )}
        </>
    );
};

const ProjectDetailsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    .edit-details {
        text-decoration: underline;
        height: 100%;
    }
`;

const ProjectDetailsRead = styled.div`
    display: grid;
    grid-template-columns: max-content max-content;
    grid-column-gap: 20px;
    grid-row-gap: 15px;

    h5 {
        grid-column: 1;
    }

    p {
        grid-column: 2;
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }
`;

const ProjectDetailsEdit = styled.div`
    ${muiInputFormsCSS};
    .edit-name-field {
        width: 200px;
    }
`;

const ProjectDetailsEditButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: ${formGap};
`;

const AlertListName = styled(Body2)`
    font-weight: bold;
    padding-right: 3px;
    padding-left: 2px;
`;

const Body2Underlined = styled(Body2)`
    text-decoration: underline;
`;

const StyledAlert = styled(Alert)`
    display: flex;
`;

const AlertRow = styled.div`
    display: flex;
`;
